// components/modals/GoForUnlimitedQuestionsModal.jsx
// Subscription modal updated to use AppPaypalSubscriptionCreateOrder API.
// Replaces the old <form> POST approach with a proper API call → PayPal redirect flow.

import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { useNavigate } from "react-router-dom";
import LoadingOverlay from "../overlays/LoadingOverlay";
import {
  commonErrorToastMessage,
  commonSuccessToastMessage,
} from "../toastify/AllToastifyErrors";
import { upgradeToUnlimited } from "../../api/upgradeToUnlimited";
import { useSelector } from "react-redux";
import usePaypalSubscription from "../../api/usePaypalSubscription.js";

// ─── Plan configuration ───────────────────────────────────────────────────────
// Map each plan to its PayPal Plan ID and display details.
// Replace PlanId values with your live PayPal Plan IDs from the .env / dashboard.
const PLANS = [
  {
    key: "annual",
    label: "Annual",
    price: "$99.99",
    amount: "99.99",
    PlanId: process.env.REACT_APP_PAYPAL_PLAN_ID_ANNUAL || "P-0CJ434567A9539157NBYN5CA",
    tooltip: "Best value — save over 40%",
  },
  {
    key: "quarterly",
    label: "Quarterly",
    price: "$29.99",
    amount: "29.99",
    PlanId: process.env.REACT_APP_PAYPAL_PLAN_ID_QUARTERLY || "P-05799457JN448591YNBYN4SI",
    tooltip: "Flexible — renews every 3 months",
  },
  {
    key: "monthly",
    label: "Monthly",
    price: "$14.99",
    amount: "14.99",
    PlanId: process.env.REACT_APP_PAYPAL_PLAN_ID_MONTHLY || "P-8F463117SV747581MNBYN34Q",
    tooltip: "Month-to-month, cancel anytime",
  },
];

// ─── Component ────────────────────────────────────────────────────────────────
const GoForUnlimitedQuestionsModal = ({ open, onClose }) => {
  const { user, subscription } = useSelector((state) => state.users);
  const navigate = useNavigate();

  // PayPal hook — handles API call + redirect
  const { initiateSubscription, isLoading: paypalLoading, error: paypalError } =
    usePaypalSubscription();

  // Local loading (OpenAI key submit)
  const [isLoading, setIsLoading] = useState(false);

  const [email] = useState(localStorage.getItem("email") || "");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [openAIKey, setOpenAIKey] = useState("");
  const [confirmOpenAIKey, setConfirmOpenAIKey] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [upgradeToUnlimitedClicked, setUpgradeToUnlimitedClicked] = useState(false);
  const [isFront, setIsFront] = useState(true);
  const [subscriptionPlan, setSubscriptionPlan] = useState("");
  const [selectedPlanKey, setSelectedPlanKey] = useState(null); // tracks which plan button is loading

  const remainingQuestionSets = 25 - Number(localStorage.getItem("UserAttempts") || 0);
  const userQuestionsAttempts = localStorage.getItem("UserAttempts");
  const usedQuestionSets = localStorage.getItem("UserAttempts");
  const monthEndDate = localStorage.getItem("monthEndDate");

  const getFormattedMonth = (dateString) => {
    const date = new Date(dateString);
    const options = { month: "short", year: "numeric", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };
  const formattedMonthEndDate = getFormattedMonth(monthEndDate);

  // Resolve subscription plan label from localStorage
  useEffect(() => {
    const planNumber = parseInt(localStorage.getItem("subscriptionPlan"));
    const planMap = { 1: "Monthly", 3: "Quarterly", 12: "Annual" };
    setSubscriptionPlan(planMap[planNumber] || "Unknown Plan");
  }, []);

  // Sync openAI key error
  useEffect(() => {
    if (openAIKey && confirmOpenAIKey) {
      setErrorMessage(
        openAIKey === confirmOpenAIKey
          ? ""
          : "Open AI Key and Confirm Open AI Key does not match."
      );
    }
  }, [openAIKey, confirmOpenAIKey]);

  // Show PayPal API errors as toasts
  useEffect(() => {
    if (paypalError) commonErrorToastMessage(paypalError);
  }, [paypalError]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleModalClose = () => {
    setIsFront(true);
    setSelectedPlanKey(null);
    onClose();
  };

  const handleGoUnlimited = () => {
    setUpgradeToUnlimitedClicked(true);
    setIsFront(false);
    setErrorMessage("");
    setOpenAIKey("");
    setConfirmOpenAIKey("");
  };

  const handleQuillionzProModal = () => {
    setIsFront(false);
    setUpgradeToUnlimitedClicked(false);
  };

  const handleGoBack = () => setIsFront(true);

  /**
   * Called when the user clicks one of the plan buttons (Annual / Quarterly / Monthly).
   * Reads user details from Redux / localStorage and calls the PayPal API.
   * On success the browser is redirected to PayPal; on failure a toast appears.
   */
  const handlePlanSelect = async (plan) => {
    setSelectedPlanKey(plan.key);

    // Gather user info — adjust field names to match your Redux store shape
    const userId = user?.Id || user?.id || localStorage.getItem("userId");
    const firstName = user?.FirstName || user?.firstName || "";
    const lastName = user?.LastName || user?.lastName || "";
    const version = user?.Version || user?.version || "GPT";

    if (!userId) {
      commonErrorToastMessage("User information is missing. Please log in again.");
      setSelectedPlanKey(null);
      return;
    }

    await initiateSubscription({
      UserId: userId,
      FirstName: firstName,
      LastName: lastName,
      Email: email,
      Amount: plan.amount,
      Version: version,
      PlanId: plan.PlanId,
      // ReturnUrl and CancelUrl default to <origin>/payment-success and /payment-cancel
      // inside the hook — override here if you need a different path
    });

    // If initiateSubscription throws (paypalError state is set), we reset the button.
    // On success the page navigates away, so this only runs on error.
    setSelectedPlanKey(null);
  };

  const handleOpenAIKeySubmit = async () => {
    if (openAIKey !== confirmOpenAIKey) {
      commonErrorToastMessage("The OpenAI key you entered does not match the confirmed OpenAI key.");
      return;
    }
    try {
      setIsLoading(true);
      const response = await upgradeToUnlimited(openAIKey, confirmOpenAIKey, email);
      localStorage.setItem("userOpenAIKey", openAIKey);
      localStorage.setItem("userGPTKey", openAIKey);
      localStorage.setItem("isGptActive", "1");
      navigate("/submitcontent");
      commonSuccessToastMessage(
        response?.data?.message ||
          "Success! Your OpenAI key has been verified. You now have unlimited question sets per month."
      );
      setOpenAIKey("");
      setConfirmOpenAIKey("");
      onClose();
    } catch (error) {
      commonErrorToastMessage("Please provide a valid OpenAI key.");
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => setIsPasswordVisible((v) => !v);
  const toggleConfirmPasswordVisibility = () => setIsConfirmPasswordVisible((v) => !v);

  const dummyPasswordField = (
    <input type="password" autoComplete="new-password" style={{ display: "none" }} readOnly />
  );

  // ── Subscription plan panel (replaces old PayPal <form> approach) ─────────
  const SubscriptionPanel = ({ showBackButton }) => (
    <div className="upgradeToProBackContent">
      <div className="bg-white shadow-2xl rounded animate-fade-slide-down px-2 pt-2 pb-5">
        {/* Header */}
        <div className="flex place-content-between mx-2 p-3 border-b">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            {showBackButton && (
              <Tooltip title="Go Back" arrow placement="top" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                <button
                  className="text-[#6c757d] text-lg pe-2 transition-all ease-in-out duration-500"
                  onClick={handleGoBack}
                >
                  <i className="fa-solid fa-arrow-left pe-3" />
                </button>
              </Tooltip>
            )}
            Subscribe To:
          </h2>
          <Tooltip title="Close" arrow placement="top" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
            <button
              className="text-[#6c757d] text-lg hover:bg-[#edeeef] px-2 rounded-full transition-all ease-in-out duration-500"
              onClick={handleModalClose}
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </Tooltip>
        </div>

        {/* Plan buttons */}
        <div className="flex flex-col text-center px-6 py-4 gap-3">
          {PLANS.map((plan, idx) => (
            <React.Fragment key={plan.key}>
              <Tooltip title={plan.tooltip} arrow placement="top" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                <button
                  type="button"
                  disabled={paypalLoading && selectedPlanKey === plan.key}
                  onClick={() => handlePlanSelect(plan)}
                  className="mt-2 hover:opacity-90 bg-[#f4833d] text-white px-4 py-2 rounded w-full disabled:opacity-60 disabled:cursor-not-allowed transition-all ease-in-out duration-300 flex items-center justify-center gap-2"
                >
                  {paypalLoading && selectedPlanKey === plan.key ? (
                    <>
                      <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      Redirecting to PayPal…
                    </>
                  ) : (
                    `${plan.label} (${plan.price})`
                  )}
                </button>
              </Tooltip>

              {/* Divider between plans */}
              {idx < PLANS.length - 1 && (
                <div className="italic opacity-50 text-sm">------ OR ------</div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Loading overlay for other operations */}
        {isLoading && <LoadingOverlay />}
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="subscription-modal-title"
      className="flex place-content-center items-center fixed"
      BackdropProps={{ style: { pointerEvents: "none" } }}
      disableEscapeKeyDown
    >
      <div
        className={`rounded-lg animate-fade-slide-down modal-content shadow-xl hover:shadow-myShadow2 ${
          isFront ? "unflipped" : "flipped"
        }`}
      >
        {/* ── FRONT: Available question sets info ── */}
        {isFront ? (
          <div className="bg-white shadow-lg text-[#212529] p-6 rounded-lg animate-fade-slide-down w-max">
            <div className="flex place-content-between border-b pb-5">
              <h2 className="text-xl font-semibold">Available Question Sets</h2>
              <Tooltip title="Close" arrow placement="top" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                <button
                  className="text-[#6c757d] text-lg hover:bg-[#edeeef] px-2 rounded-full transition-all ease-in-out duration-500"
                  onClick={handleModalClose}
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </Tooltip>
            </div>

            <div className="py-6 border-b">
              <p>
                <i className="fa-solid fa-circle-info text-blue-400 pe-2" />
                You have{" "}
                <span className="font-semibold">{usedQuestionSets} out of 25 question sets</span>{" "}
                available for this month till{" "}
                <span className="font-semibold">{formattedMonthEndDate}.</span>
              </p>

              {!user?.isFree && (
                <p className="mt-5">
                  <i className="fa-solid fa-circle-info text-blue-400 pe-2" />
                  For more question sets, upgrade to the{" "}
                  <span className="font-semibold">Unlimited plan</span> by sharing your OpenAI Secured Key.
                </p>
              )}
            </div>

            <div className="flex place-content-between gap-8">
              <Tooltip title="Manage Subscription" arrow placement="top" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                <button
                  className="mt-4 px-6 py-2 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] transition-all ease-in-out duration-500"
                  type="button"
                  onClick={handleQuillionzProModal}
                >
                  Manage Subscription
                </button>
              </Tooltip>

              {!user?.isFree && !user?.isPro && (
                <Tooltip title="Upgrade to Unlimited" arrow placement="top" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                  <button
                    className="mt-4 px-6 py-2 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] transition-all ease-in-out duration-500"
                      type="button"
                      onClick={handleGoUnlimited}
                  >
                    Upgrade to Unlimited
                  </button>
                </Tooltip>
              )}
            </div>
          </div>

        /* ── BACK: Upgrade to unlimited (OpenAI key entry) ── */
        ) : upgradeToUnlimitedClicked ? (
          <div className="bg-white back-content shadow-lg text-[#212529] p-6 rounded-lg animate-fade-slide-down w-[35vw]">
            <div className="flex place-content-between pb-5">
              <h2 className="text-xl font-semibold">
                <Tooltip title="Go Back" arrow placement="top" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                  <button
                    className="text-[#6c757d] text-lg pe-2 transition-all ease-in-out duration-500"
                    onClick={handleGoBack}
                  >
                    <i className="fa-solid fa-arrow-left pe-3" />
                  </button>
                </Tooltip>
                Upgrade to unlimited plan
              </h2>
              <Tooltip title="Close" arrow placement="top" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                <button
                  className="text-[#6c757d] text-lg hover:bg-[#edeeef] px-2 rounded-full transition-all ease-in-out duration-500"
                  onClick={handleModalClose}
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </Tooltip>
            </div>

            <div className="pt-1">
              <p className="opacity-60 italic pb-3">
                <i className="fa-solid fa-circle-info text-blue-400 pe-2" />
                For unlimited question sets, kindly provide your OpenAI Key below.
                This key will be used securely under your existing subscription plan.
              </p>

              <p className="mt-3">
                <span>Email : </span>
                <span className="px-2 font-semibold">{email}</span>
              </p>

              {dummyPasswordField}

              {/* OpenAI Key input */}
              <p className="flex px-2 mt-5 border border-[#fcd6bf] rounded-lg outline-[#f4833d]">
                <input
                  type={isPasswordVisible ? "text" : "password"}
                  placeholder="Enter Your Open AI Key"
                  className="p-2 outline-none w-full bg-none"
                  value={openAIKey}
                  onChange={(e) => setOpenAIKey(e.target.value)}
                />
                <Tooltip
                  title={isPasswordVisible ? "Hide API Key" : "Show API Key"}
                  arrow
                  placement="top"
                  TransitionComponent={Zoom}
                  TransitionProps={{ timeout: 300 }}
                >
                  <button onClick={togglePasswordVisibility} className="text-[#f4833d] px-3 transition-all ease-in-out duration-500">
                    <i className={`fa-solid ${isPasswordVisible ? "fa-eye" : "fa-eye-slash"}`} />
                  </button>
                </Tooltip>
              </p>

              {/* Confirm OpenAI Key input */}
              <p className="flex mt-5 px-2 border border-[#fcd6bf] rounded-lg outline-[#f4833d]">
                <input
                  type={isConfirmPasswordVisible ? "text" : "password"}
                  placeholder="Confirm Your Open AI Key"
                  className="p-2 outline-none w-full"
                  value={confirmOpenAIKey}
                  onChange={(e) => setConfirmOpenAIKey(e.target.value)}
                />
                <Tooltip
                  title={isConfirmPasswordVisible ? "Hide API Key" : "Show API Key"}
                  arrow
                  placement="top"
                  TransitionComponent={Zoom}
                  TransitionProps={{ timeout: 300 }}
                >
                  <button onClick={toggleConfirmPasswordVisibility} className="text-[#f4833d] px-3 transition-all ease-in-out duration-500">
                    <i className={`fa-solid ${isConfirmPasswordVisible ? "fa-eye" : "fa-eye-slash"}`} />
                  </button>
                </Tooltip>
              </p>

              {errorMessage && (
                <p className="text-red-400 mb-6 text-sm pt-1 animate-fade-slide-down">
                  <i className="fa-solid fa-circle-exclamation text-red-500 text-[13px] mt-2" />
                  <span className="px-2 text-red-500 text-[13px] mt-2">{errorMessage}</span>
                </p>
              )}
            </div>

            <div className="flex place-content-end gap-8">
              <Tooltip title="Cancel" arrow placement="top" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                <button
                  className="rounded mt-4 px-6 py-2 hover:opacity-90 text-[#6c757d] hover:bg-[#6c757d] hover:border-[#6c757d] hover:text-white border border-[#6c757d] transition-all ease-in-out duration-500"
                  onClick={handleModalClose}
                >
                  Cancel
                </button>
              </Tooltip>
              <Tooltip title="Submit" arrow placement="top" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                <button
                  className="mt-4 px-6 py-2 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] transition-all ease-in-out duration-500"
                  onClick={handleOpenAIKeySubmit}
                >
                  Submit
                </button>
              </Tooltip>
            </div>

            {isLoading && <LoadingOverlay />}
          </div>

        /* ── BACK: Subscription plan selection ── */
        ) : (
          <SubscriptionPanel showBackButton={remainingQuestionSets !== 0} />
        )}

        {/* Global loading overlay */}
        {isLoading && <LoadingOverlay />}
      </div>
    </Modal>
  );
};

export default GoForUnlimitedQuestionsModal;


// import React, { useEffect, useState } from "react";
// import Modal from "@mui/material/Modal";
// import Tooltip from "@mui/material/Tooltip";
// import Zoom from "@mui/material/Zoom";
// import { useNavigate } from "react-router-dom";
// import LoadingOverlay from "../overlays/LoadingOverlay";
// import {
//   commonErrorToastMessage,
//   commonSuccessToastMessage,
// } from "../toastify/AllToastifyErrors";
// import { upgradeToUnlimited } from "../../api/upgradeToUnlimited";
// import {
//   monthlyForm,
//   quaterlyForm,
//   yearlyForm,
// } from "../../utils/SubmitContent/PaypalForms";
// import { useSelector } from "react-redux";
// import usePaypalSubscription from "../../api/usePaypalSubscription.js";

// const GoForUnlimitedQuestionsModal = ({ open, onClose }) => {
//   const { user, subscription } = useSelector((state) => state.users);

//   const navigate = useNavigate();

//   // Add a new state variable to track the loading state
//   const [isLoading, setIsLoading] = React.useState(false);

//   // 1. Create two state variables
//   const [email, setEmail] = useState(localStorage.getItem("email"));

//   const [isPasswordVisible, setIsPasswordVisible] = useState(false);
//   const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
//     useState(false);

//   const [openAIKey, setOpenAIKey] = useState("");
//   const [confirmOpenAIKey, setConfirmOpenAIKey] = useState("");
//   const [errorMessage, setErrorMessage] = useState("");
//   const [upgradeToUnlimitedClicked, setUpgradeToUnlimitedClicked] =
//     useState(false);

//   const remainingQuestionSets = 25 - localStorage.getItem("UserAttempts");
//   const userQuestionsAttempts = localStorage.getItem("UserAttempts");
//   const usedQuestionSets = localStorage.getItem("UserAttempts");
//   const monthEndDate = localStorage.getItem("monthEndDate");

//   const getFormattedMonth = (dateString) => {
//     const date = new Date(dateString);
//     const options = { month: "short", year: "numeric", day: "numeric" };
//     return date.toLocaleDateString("en-US", options);
//   };

//   // Assuming the value of monthEndDate is in 'MM/DD/YYYY' format
//   const formattedMonthEndDate = getFormattedMonth(monthEndDate);

//   // Function to handle the "Close" button click
//   const handleModalClose = () => {
//     // setEmail("");
//     setIsFront(true);
//     onClose(); // Close the modal without processing the input
//   };

//   const [isFront, setIsFront] = useState(true); // Add this state to manage front/back view

//   const handleGoUnlimited = () => {
//     setUpgradeToUnlimitedClicked(true);
//     setIsFront(false); // Switch to the back view when clicking "Unlimited" button
//     setErrorMessage("");
//     setOpenAIKey("");
//     setConfirmOpenAIKey("");
//   };

//   const handleQuillionzProModal = () => {
//     setIsFront(false);
//     setUpgradeToUnlimitedClicked(false);
//     setSubscriptionPlan(true);
//   };

//   useEffect(() => {
//     if (openAIKey && confirmOpenAIKey) {
//       setErrorMessage(
//         openAIKey === confirmOpenAIKey
//           ? ""
//           : "Open AI Key and Confirm Open AI Key does not match."
//       );
//     }
//   }, [openAIKey, confirmOpenAIKey]);
//   const handleGoBack = () => {
//     setIsFront(true); // Switch to the back view when clicking "Unlimited" button
//   };

//   const handleOpenAIKeySubmit = async () => {
//     if (openAIKey === confirmOpenAIKey) {
//       try {
//         setIsLoading(true);
//         setIsFront(false); // Switch to the back view when clicking "Unlimited" button

//         const response = await upgradeToUnlimited(
//           openAIKey,
//           confirmOpenAIKey,
//           email
//         );

//         localStorage.setItem("userOpenAIKey", openAIKey);
//         localStorage.setItem("userGPTKey", openAIKey);
//         localStorage.setItem("isGptActive", "1");

//         navigate("/submitcontent");

//         commonSuccessToastMessage(
//           response?.data?.message ||
//             "Success! Your OpenAI key has been successfully verified and saved. You now have unlimited questions sets per month."
//         );
//         setIsLoading(false);
//         setErrorMessage("");
//         setOpenAIKey("");
//         setConfirmOpenAIKey("");
//         onClose();
//       } catch (error) {
//         setIsLoading(false);
//         commonErrorToastMessage("Please provide a valid OpenAI key.");

//         console.log("Error while submitting your GPT key : ", error);
//         setErrorMessage("");
//         setOpenAIKey("");
//         setConfirmOpenAIKey("");
//       }
//     } else {
//       commonErrorToastMessage(
//         "The OpenAI key you entered does not match the confirmed OpenAI key."
//       );
//     }
//   };

//   const togglePasswordVisibility = () => {
//     setIsPasswordVisible(!isPasswordVisible);
//   };

//   const toggleConfirmPasswordVisibility = () => {
//     setIsConfirmPasswordVisible(!isConfirmPasswordVisible);
//   };

//   const dummyPasswordField = (
//     <input
//       type="password"
//       autoComplete="new-password"
//       style={{ display: "none" }}
//       readOnly
//     />
//   );

//   // Plan expired message logic :
//   const [subscriptionPlan, setSubscriptionPlan] = useState("");

//   // Effect hook to update subscription plan name based on the value
//   useEffect(() => {
//     const planNumber = parseInt(localStorage.getItem("subscriptionPlan"));
//     let planName = "";

//     switch (planNumber) {
//       case 1:
//         planName = "Monthly";
//         break;
//       case 3:
//         planName = "Quarterly";
//         break;
//       case 12:
//         planName = "Annual";
//         break;
//       default:
//         planName = "Unknown Plan";
//         break;
//     }

//     setSubscriptionPlan(planName);
//   }, []); // Empty dependency array ensures this effect runs only once when the component mounts.

//   return (
//     <Modal
//       open={open}
//       onClose={onClose}
//       aria-labelledby="youtube-modal-title"
//       // Apply the style to the modal container
//       className="flex place-content-center items-center fixed"
//       // Disable clicking on the backdrop and Escape key
//       BackdropProps={{
//         style: { pointerEvents: "none" },
//       }}
//       disableEscapeKeyDown
//     >
//       <div
//         className={`rounded-lg animate-fade-slide-down modal-content shadow-xl hover:shadow-myShadow2 ${
//           isFront ? "unflipped" : "flipped"
//         }`} /* Updated this line */
//       >
//         {isFront ? (
//           <div
//             className={`bg-white shadow-lg text-[#212529] p-6 rounded-lg   animate-fade-slide-down w-max`}
//           >
//             <div className="flex justify-end cursor-pointer text-2xl mb-2 text-red-600  "></div>

//             <div className="flex place-content-between border-b pb-5">
//               <h2 className=" text-xl font-semibold ">
//                 Available Question Sets
//               </h2>
//               <Tooltip
//                 title="Close"
//                 arrow
//                 placement="top"
//                 TransitionComponent={Zoom}
//                 TransitionProps={{ timeout: 300 }}
//               >
//                 <button
//                   className="text-[#6c757d] text-lg hover:bg-[#edeeef] px-2 rounded-full transition-all ease-in-out duration-500"
//                   onClick={handleModalClose}
//                 >
//                   <i className="fa-solid fa-xmark"></i>{" "}
//                 </button>
//               </Tooltip>
//             </div>

//             <div className="py-6 border-b ">
//               <p className="">
//                 <span>
//                   <span>
//                     <i className="fa-solid fa-circle-info text-blue-400 pe-2"></i>
//                   </span>
//                   {userQuestionsAttempts !== 0 &&
//                   subscription?.isPlanExpired ? (
//                     <span>
//                       You have{" "}
//                       <span className="font-semibold">
//                         <span className="font-semibold">
//                           {usedQuestionSets}
//                         </span>{" "}
//                         out of 25 questions sets
//                       </span>{" "}
//                       available for this month till{" "}
//                       <span className="font-semibold">
//                         {formattedMonthEndDate}.
//                       </span>{" "}
//                     </span>
//                   ) : (
//                     <span>
//                       You have{" "}
//                       <span className="font-semibold">
//                         <span className="font-semibold">
//                           {usedQuestionSets}
//                         </span>{" "}
//                         out of 25 questions sets
//                       </span>{" "}
//                       available for this month till{" "}
//                       <span className="font-semibold">
//                         {formattedMonthEndDate}.
//                       </span>{" "}
//                     </span>
//                   )}
//                 </span>
//                 {""}
//               </p>

//               {!user.isFree && !user.isPro && (
//                 <p className="mt-5">
//                   <span>
//                     <span>
//                       <i className="fa-solid fa-circle-info text-blue-400 pe-2"></i>
//                     </span>
//                     For more questions sets, upgrade to the{" "}
//                     <span className=" font-semibold">Unlimited plan</span> by
//                     sharing your OpenAI Secured Key.
//                   </span>
//                 </p>
//               )}
//             </div>

//             <div className="flex place-content-between gap-8">
//               <Tooltip
//                 title="Manage Subscription"
//                 arrow
//                 placement="top"
//                 TransitionComponent={Zoom}
//                 TransitionProps={{ timeout: 300 }}
//               >
//                 <button
//                   className="mt-4 px-6 py-2 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] transition-all ease-in-out duration-500"
//                   onClick={handleQuillionzProModal}
//                 >
//                   Manage Subscription
//                 </button>
//               </Tooltip>

//               {!user.isFree && !user.isPro && (
//                 <Tooltip
//                   title="Upgrade to Unlimited"
//                   arrow
//                   placement="top"
//                   TransitionComponent={Zoom}
//                   TransitionProps={{ timeout: 300 }}
//                 >
//                   <button
//                     className="mt-4 px-6 py-2 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] transition-all ease-in-out duration-500"
//                     onClick={handleGoUnlimited}
//                   >
//                     Upgrade to Unlimited
//                   </button>
//                 </Tooltip>
//               )}
//             </div>
//           </div>
//         ) : remainingQuestionSets != 0 && !upgradeToUnlimitedClicked ? (
//           <div className="upgradeToProBackContent  ">
//             <div className="bg-white  shadow-2xl rounded  animate-fade-slide-down px-2 pt-2 pb-5 h-[48%]">
//               <div className="flex place-content-between mx-2 pl-[1rem] pr-[1rem] border-b">
//                 <h2 className="text-xl font-semibold">
//                   <span className="pr-[4rem] ">Subscribe To : </span>
//                 </h2>

//                 <Tooltip
//                   title="Close"
//                   arrow
//                   placement="top"
//                   TransitionComponent={Zoom}
//                   TransitionProps={{ timeout: 300 }}
//                 >
//                   <button
//                     className="text-[#6c757d] text-lg hover:bg-[#edeeef] px-2 rounded-full transition-all ease-in-out duration-500"
//                     onClick={handleModalClose}
//                   >
//                     <i className="fa-solid fa-xmark"></i>{" "}
//                   </button>
//                 </Tooltip>
//               </div>

//               {/* TODO : Piyush ji, the actual Paypal Payment Links for subscriptions have not been incorporated yet. */}
//               {/* Subscribe ($99.99/Year) Button */}
//               {process.env.REACT_APP_TEST_MODE && (
//                 <div className="  text-center px-[0.5rem] py-2 pb-[5px] ">
//                   <div>
//                     <div className="w-full">
//                       <Tooltip
//                         title="Annual ($99.99)"
//                         arrow
//                         placement="top"
//                         TransitionComponent={Zoom}
//                         TransitionProps={{ timeout: 300 }}
//                       >
//                         {yearlyForm(email)}
//                       </Tooltip>
//                     </div>

//                     <div className="mt-1 italic opacity-[50%] text-xs">
//                       ------------ OR ------------
//                     </div>
//                   </div>

//                   {/* Subscribe ($29.99/Quarter) Button*/}
//                   <div>
//                     <div>
//                       <Tooltip
//                         title="Quarterly ($29.99)"
//                         arrow
//                         placement="top"
//                         TransitionComponent={Zoom}
//                         TransitionProps={{ timeout: 300 }}
//                       >
//                         {quaterlyForm(email)}
//                       </Tooltip>
//                     </div>

//                     <div className="mt-1 italic opacity-[50%] text-xs">
//                       ------------ OR ------------
//                     </div>
//                   </div>

//                   {/* Subscribe ($14.99/Month) Button*/}
//                   <div className="pb-3">
//                     <Tooltip
//                       title=" Monthly ($14.99)"
//                       arrow
//                       placement="top"
//                       TransitionComponent={Zoom}
//                       TransitionProps={{ timeout: 300 }}
//                     >
//                       {monthlyForm(email)}
//                     </Tooltip>
//                   </div>
//                 </div>
//               )}

//               {!process.env.REACT_APP_TEST_MODE && (
//                 <div className="flex flex-col text-center px-6 py-2 ">
//                   <button
//                     type="button"
//                     className="mt-2 hover:opacity-90 bg-[#f4833d] text-white px-4 py-2 rounded w-full"
//                   >
//                   <Tooltip
//                         title="Annual ($99.99)"
//                         arrow
//                         placement="top"
//                         TransitionComponent={Zoom}
//                         TransitionProps={{ timeout: 300 }}
//                       >
//                         {yearlyForm(email)}
//                       </Tooltip>
//                   </button>
//                   <div className="mt-1 italic opacity-[50%] text-sm">
//                     ------ OR ------
//                   </div>
//                   <button
//                     type="button"
//                     className="mt-2 hover:opacity-90 bg-[#f4833d] text-white px-4 py-2 rounded w-full"
//                   >
//                   <Tooltip
//                         title="Quarterly ($29.99)"
//                         arrow
//                         placement="top"
//                         TransitionComponent={Zoom}
//                         TransitionProps={{ timeout: 300 }}
//                       >
//                         {quaterlyForm(email)}
//                   </Tooltip>
//                   </button>
//                   <div className="mt-1 italic opacity-[50%] text-sm">
//                     ------ OR ------
//                   </div>
//                   <button
//                     type="button"
//                     className="mt-2 hover:opacity-90 bg-[#f4833d] text-white px-4 py-2 rounded w-full"
//                   >
//                   <Tooltip
//                       title=" Monthly ($14.99)"
//                       arrow
//                       placement="top"
//                       TransitionComponent={Zoom}
//                       TransitionProps={{ timeout: 300 }}
//                     >
//                       {monthlyForm(email)}
//                     </Tooltip>
//                   </button>
//                 </div>
//               )}

//               {/* Loading overlay */}
//               {isLoading && <LoadingOverlay />}
//             </div>
//           </div>
//         ) : upgradeToUnlimitedClicked ? (
//           <div className="bg-white back-content shadow-lg text-[#212529] p-6 rounded-lg   animate-fade-slide-down w-[35vw]">
//             {/* <div className="flex justify-end cursor-pointer text-2xl mb-2 text-red-600  "></div> */}

//             <div className="flex place-content-between  pb-5">
//               <h2 className="text-xl font-semibold">
//                 <Tooltip
//                   title="Go Back"
//                   arrow
//                   placement="top"
//                   TransitionComponent={Zoom}
//                   TransitionProps={{ timeout: 300 }}
//                 >
//                   <button
//                     className="text-[#6c757d] text-lg pe-2 transition-all ease-in-out duration-500"
//                     onClick={handleGoBack}
//                   >
//                     <span>
//                       <i className="fa-solid fa-arrow-left pe-3"></i>{" "}
//                     </span>
//                   </button>{" "}
//                 </Tooltip>
//                 <span>Upgrade to unlimited plan </span>
//               </h2>
//               <div style={{ display: "inline-block" }}>
//                 <Tooltip
//                   title="Close"
//                   arrow
//                   placement="top"
//                   TransitionComponent={Zoom}
//                   TransitionProps={{ timeout: 300 }}
//                   // Add the following two lines
//                   anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//                   transformOrigin={{ vertical: "top", horizontal: "right" }}
//                 >
//                   <button
//                     className="text-[#6c757d] text-lg hover:bg-[#edeeef] px-2 rounded-full transition-all ease-in-out duration-500"
//                     onClick={handleModalClose}
//                   >
//                     <i className="fa-solid fa-xmark"></i>{" "}
//                   </button>
//                 </Tooltip>
//               </div>
//             </div>

//             <div className="pt-1">
//               <p className="opacity-60 italic pb-3">
//                 <span>
//                   <i className="fa-solid fa-circle-info text-blue-400 pe-2"></i>
//                 </span>
//                 For unlimited questions sets, kindly provide your OpenAI Key
//                 below. This key will be used securely under your existing
//                 subscription plan.
//               </p>

//               <p className="mt-3">
//                 <span className="">Email : </span>
//                 <span className="px-2 font-semibold">{email}</span>
//               </p>
//               {dummyPasswordField}
//               <p className="flex px-2 mt-5 border border-[#fcd6bf] rounded-lg outline-[#f4833d] ">
//                 <input
//                   type={isPasswordVisible ? "text" : "password"}
//                   placeholder="Enter Your Open AI Key"
//                   className="p-2 outline-none w-full bg-none"
//                   value={openAIKey}
//                   onChange={(e) => setOpenAIKey(e.target.value)}
//                 />

//                 <Tooltip
//                   title={isPasswordVisible ? "Hide API Key" : "Show API Key"}
//                   arrow
//                   key={isPasswordVisible ? "visible" : "hidden"} // add this line
//                   placement="top"
//                   TransitionComponent={Zoom}
//                   TransitionProps={{ timeout: 300 }}
//                   anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//                   transformOrigin={{ vertical: "top", horizontal: "right" }}
//                 >
//                   <button
//                     onClick={togglePasswordVisibility}
//                     className="text-[#f4833d] px-3 transition-all ease-in-out duration-500"
//                     key={isPasswordVisible ? "visible" : "hidden"} // add this line
//                   >
//                     {isPasswordVisible ? (
//                       <i className="fa-solid fa-eye"></i>
//                     ) : (
//                       <i className="fa-solid fa-eye-slash"></i>
//                     )}
//                   </button>
//                 </Tooltip>
//               </p>

//               <p
//                 className={`flex mt-5 px-2 border border-[#fcd6bf] rounded-lg outline-[#f4833d] `}
//               >
//                 <input
//                   type={isConfirmPasswordVisible ? "text" : "password"}
//                   placeholder="Confirm Your Open AI Key"
//                   className="p-2 outline-none w-full"
//                   value={confirmOpenAIKey}
//                   onChange={(e) => setConfirmOpenAIKey(e.target.value)}
//                 />

//                 <Tooltip
//                   title={
//                     isConfirmPasswordVisible ? "Hide API Key" : "Show API Key"
//                   }
//                   arrow
//                   key={isConfirmPasswordVisible ? "visible" : "hidden"} // add this line
//                   placement="top"
//                   TransitionComponent={Zoom}
//                   TransitionProps={{ timeout: 300 }}
//                   anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//                   transformOrigin={{ vertical: "top", horizontal: "right" }}
//                 >
//                   <button
//                     onClick={toggleConfirmPasswordVisibility}
//                     className="text-[#f4833d] px-3 transition-all ease-in-out duration-500"
//                     key={isConfirmPasswordVisible ? "visible" : "hidden"}
//                   >
//                     {isConfirmPasswordVisible ? (
//                       <i className="fa-solid fa-eye"></i>
//                     ) : (
//                       <i className="fa-solid fa-eye-slash"></i>
//                     )}
//                   </button>
//                 </Tooltip>
//               </p>
//               {errorMessage && (
//                 <p className="text-red-400 mb-6 text-sm pt-1 animate-fade-slide-down transition-all ease-in-out duration-500 ">
//                   <i className="fa-solid fa-circle-exclamation  text-red-500   text-[13px] mt-2"></i>
//                   <span className="px-2 text-red-500   text-[13px] mt-2">
//                     {errorMessage}
//                   </span>
//                 </p>
//               )}
//             </div>

//             <div className="flex place-content-end gap-8">
//               <Tooltip
//                 title="Cancel"
//                 arrow
//                 placement="top"
//                 TransitionComponent={Zoom}
//                 TransitionProps={{ timeout: 300 }}
//               >
//                 <button
//                   className={`rounded mt-4 px-6 py-2 hover:opacity-90 text-[#6c757d] hover:bg-[#6c757d] hover:border-[#6c757d] hover:text-white border border-[#6c757d]  transition-all ease-in-out duration-500`}
//                   onClick={handleModalClose}
//                 >
//                   Cancel
//                 </button>
//               </Tooltip>

//               <Tooltip
//                 title="Submit"
//                 arrow
//                 placement="top"
//                 TransitionComponent={Zoom}
//                 TransitionProps={{ timeout: 300 }}
//               >
//                 <button
//                   className="mt-4 px-6 py-2 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] transition-all ease-in-out duration-500"
//                   onClick={handleOpenAIKeySubmit}
//                 >
//                   Submit
//                 </button>
//               </Tooltip>
//             </div>
//           </div>
//         ) : (
//           <div className="upgradeToProBackContent  ">
//             <div className="bg-white  shadow-2xl rounded  animate-fade-slide-down px-2 pt-2 pb-5 h-[48%]">
//               <div className="flex place-content-between mx-2 p-3 border-b">
//                 <h2 className="text-xl font-semibold">
//                   <Tooltip
//                     title="Go Back"
//                     arrow
//                     placement="top"
//                     TransitionComponent={Zoom}
//                     TransitionProps={{ timeout: 300 }}
//                   >
//                     <button
//                       className="text-[#6c757d] text-lg pe-2 transition-all ease-in-out duration-500"
//                       onClick={handleGoBack}
//                     >
//                       <span>
//                         <i className="fa-solid fa-arrow-left pe-3"></i>{" "}
//                       </span>
//                     </button>{" "}
//                   </Tooltip>
//                   <span>Subscribe To : </span>
//                 </h2>

//                 <Tooltip
//                   title="Close"
//                   arrow
//                   placement="top"
//                   TransitionComponent={Zoom}
//                   TransitionProps={{ timeout: 300 }}
//                 >
//                   <button
//                     className="text-[#6c757d] text-lg hover:bg-[#edeeef] px-2 rounded-full transition-all ease-in-out duration-500"
//                     onClick={handleModalClose}
//                   >
//                     <i className="fa-solid fa-xmark"></i>{" "}
//                   </button>
//                 </Tooltip>
//               </div>

//               {/* Loading overlay */}
//               {isLoading && <LoadingOverlay />}
//             </div>
//           </div>
//         )}

//         {/* Loading overlay */}
//         {isLoading && <LoadingOverlay />}
//       </div>
//     </Modal>
//   );
// };
// export default GoForUnlimitedQuestionsModal;
